import type { AppearanceRow, api, Features, Slice } from '@setplex/tria-api'
import type {
  AppearanceBanner,
  AppearanceComponent,
  AppearanceModule,
} from '@setplex/tria-api/src/entities/appearance'
import type { EffectParams } from 'effector'
import type { VariantOf } from 'variant'
import type { options } from '~/core/flags'
import type { ModuleFallbackFlags } from '~/shared/config/modules'

import { nil, onTerms, payload, variant } from 'variant'

export type RowsPage = Slice<AppearanceRow>

/** Where module rows came from: API vs client-built defaults/fallback */
export type StructureOrigin = 'backend' | 'client'

export type ComponentsPage = Slice<AppearanceComponent> & {
  structureOrigin: StructureOrigin
}

export type TGetComponentsFx = {
  source: {
    features: Features
    options: options.Options
    modules: ModuleFallbackFlags
    appearanceModules: AppearanceModule[] | null
    identifire: string | null
    isGuest: boolean | null
  }
  params: Required<EffectParams<typeof api.appearance.getModuleComponentsFx>>
}

export type CachedModuleStructure = {
  structure: ModuleStructure | null
  structureOrigin: StructureOrigin
}

// StructureCacheByModule = Record<Identifier, cached structure + origin>
export type StructureCacheByModule = Record<
  string,
  CachedModuleStructure | null
>

export type ModuleStructure = Map<string, Row>

export type RowData = VariantOf<typeof RowData>
export const RowData = variant(
  onTerms(({ T }) => ({
    Empty: nil,
    Loading: nil,
    Failed: payload<unknown>(),
    Dirty: payload(T),
    Updating: payload(T),
    Awaiting: payload(T),
    Ready: payload(T),
  }))
)

export const { Empty, Loading, Failed, Dirty, Updating, Awaiting, Ready } =
  RowData

export type Row = {
  desc: AppearanceRow
  data: RowData
  hasMore?: boolean
}

export type ViewBanner<C = unknown[]> = {
  id: string
  content: C
  isMoreThanOnePage: boolean
  appearance: AppearanceRow
  type: Row['data']['type']
}

export type ViewRow<C = unknown[]> = {
  id: string
  content: C
  isMoreThanOnePage: boolean
  appearance: AppearanceRow
  type: Row['data']['type']
}

export type ViewComponent<
  T = AppearanceRow | AppearanceBanner,
  C = unknown[],
> = {
  id: string
  content: C
  isMoreThanOnePage: boolean
  appearance: T
  type: Row['data']['type']
}
